<template>
    <div>
        <div v-if="valuationEstimate.base_valuation">
            <div class="my-4">
                <h5>Price Estimate</h5>

                <div class="text-xs my-2">
                    In
                    <span class="font-bold">{{ estimateCurrencyTitle }}</span>
                    as at <span class="font-bold">{{ dateNow }}</span>
                </div>
                <div
                    class="my-4 grid grid-cols-1 gap-0 mb-4 bg-white overflow-hidden shadow divide-y divide-gray-200 md:grid-cols-4 md:divide-y-0 md:divide-x"
                >
                    <div
                        class="col-span-1 sm:col-span-1 flex items-center justify-center bg-primaryBlue-20 p-2"
                    >
                        <h6 class="font-sm mb-0">Per Carat</h6>
                    </div>
                    <div
                        class="col-span-1 sm:col-span-1 text-center p-3 sm:p-4"
                    >
                        <h6 class="font-sm">Low Range</h6>
                        <div class="mt-4 text-sm">
                            {{
                                $fabHelper.makeMoney(
                                    valuationEstimate.per_carat_valuation *
                                        0.75,
                                    valuationEstimateResult.parameters
                                        .converted_valuation_currency
                                )
                            }}
                        </div>
                    </div>
                    <div
                        class="col-span-1 sm:col-span-1 text-center p-3 sm:p-4"
                    >
                        <h5>Estimate</h5>
                        <div class="mt-4 font-bold font-lg">
                            {{
                                $fabHelper.makeMoney(
                                    valuationEstimate.per_carat_valuation * 1,
                                    valuationEstimateResult.parameters
                                        .converted_valuation_currency
                                )
                            }}
                        </div>
                    </div>
                    <div
                        class="col-span-1 sm:col-span-1 text-center p-3 sm:p-4"
                    >
                        <h6 class="font-sm">High Range</h6>
                        <div class="mt-4 text-sm">
                            {{
                                $fabHelper.makeMoney(
                                    valuationEstimate.per_carat_valuation *
                                        1.25,
                                    valuationEstimateResult.parameters
                                        .converted_valuation_currency
                                )
                            }}
                        </div>
                    </div>
                </div>
                <div
                    class="my-4 grid grid-cols-1 gap-0 mb-4 bg-white overflow-hidden shadow divide-y divide-gray-200 md:grid-cols-4 md:divide-y-0 md:divide-x"
                >
                    <div
                        class="col-span-1 sm:col-span-1 flex items-center justify-center bg-primaryBlue-20 p-2"
                    >
                        <h6 class="font-sm mb-0">Total</h6>
                    </div>
                    <div
                        class="col-span-1 sm:col-span-1 text-center p-3 sm:p-4"
                    >
                        <h6 class="font-sm">Low Range</h6>
                        <div class="mt-4 text-sm">
                            {{
                                $fabHelper.makeMoney(
                                    valuationEstimate.base_valuation * 0.75,
                                    valuationEstimateResult.parameters
                                        .converted_valuation_currency
                                )
                            }}
                        </div>
                    </div>
                    <div
                        class="col-span-1 sm:col-span-1 text-center p-3 sm:p-4"
                    >
                        <h5>Estimate</h5>
                        <div class="mt-4 font-bold font-lg">
                            {{
                                $fabHelper.makeMoney(
                                    valuationEstimate.base_valuation * 1,
                                    valuationEstimateResult.parameters
                                        .converted_valuation_currency
                                )
                            }}
                        </div>
                    </div>
                    <div
                        class="col-span-1 sm:col-span-1 text-center p-3 sm:p-4"
                    >
                        <h6 class="font-sm">High Range</h6>
                        <div class="mt-4 text-sm">
                            {{
                                $fabHelper.makeMoney(
                                    valuationEstimate.base_valuation * 1.25,
                                    valuationEstimateResult.parameters
                                        .converted_valuation_currency
                                )
                            }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="!isEmpty(valuationChartData)">
            <h5 class="flex">
                Price Chart<Tooltip
                    v-if="resultTypeOptions[valuationType].chart.helpText"
                    :tooltip="resultTypeOptions[valuationType].chart.helpText"
                ></Tooltip>
            </h5>
            <notice notice-type="info">{{ resultPriceNotice }}</notice>
            <div class="text-xs my-2 font-bold">
                <span class="">{{ valuationDateRangeTitle }}:</span>
                {{ valuationIntervalTitle }} data from
                {{
                    $dayjs(valuationDataConfig.start_datetime).format(
                        "D MMM YYYY"
                    )
                }}
                to
                {{
                    $dayjs(valuationDataConfig.end_datetime).format(
                        "D MMM YYYY"
                    )
                }}
            </div>

            <line-chart
                :chart-data="valuationChartData"
                :valuation-currency-prefix="
                    valuationParams.converted_valuation_currency
                "
            ></line-chart>
        </div>
        <div
            class="mt-4 flex justify-center"
            v-if="
                !isEmpty(valuationIndexOptions) && !isEmpty(valuationChartData)
            "
        >
            <div class="flex-grow max-w-screen-sm">
                <div class="col-span-2 sm:col-span-2">
                    <div
                        :class="
                            'form-item select-single ' + getClass('date_range')
                        "
                    >
                        <div class="flex">
                            <label
                                >{{ valuationIndexOptions.date_range.label }}:
                            </label>
                            <Tooltip
                                v-if="getHelpText('date_range')"
                                :tooltip="getHelpText('date_range')"
                            ></Tooltip>
                        </div>
                        <select
                            v-model="newValuationRequest.date_range"
                            name="date_range"
                            class="form-select single"
                        >
                            <option value="">-- Select --</option>
                            <option
                                v-for="(option, index) in valuationIndexOptions
                                    .date_range.options"
                                :value="index"
                            >
                                {{ option.title }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
        <div class="my-4" v-if="!isEmpty(valuationSummary)">
            <h5>Chart Summary</h5>
            <div class="text-xs my-2">
                In
                <span class="font-bold">{{ valuationCurrencyTitle }}</span> as
                at <span class="font-bold">{{ dateNow }}</span>
            </div>

            <div
                class="my-4 grid grid-cols-1 gap-0 mb-0 md:grid-cols-2 md:gap-6 space-y-4 md:space-y-0"
            >
                <div class="bg-white shadow overflow-hidden sm:rounded-lg">
                    <div class="px-4 py-3 sm:px-6">
                        <p
                            class="font-bold mt-1 max-w-2xl text-sm text-gray-500"
                        >
                            Per Carat
                        </p>
                    </div>
                    <div class="border-t border-gray-200 text-xs">
                        <dl>
                            <div
                                class="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                            >
                                <dt class="font-medium text-gray-500">
                                    Minimum Price
                                </dt>
                                <dd
                                    class="mt-1 text-gray-900 sm:mt-0 sm:col-span-2"
                                >
                                    {{
                                        $fabHelper.makeMoney(
                                            valuationDataConfig.time_series_data_per_carat_min,
                                            valuationParams.converted_valuation_currency
                                        )
                                    }}
                                </dd>
                            </div>
                            <div
                                class="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                            >
                                <dt class="font-bold text-gray-500">
                                    Average Price
                                </dt>
                                <dd
                                    class="mt-1 text-gray-900 sm:mt-0 sm:col-span-2 font-bold"
                                >
                                    {{
                                        $fabHelper.makeMoney(
                                            valuationSummary.base_valuation_per_carat_mid,
                                            valuationParams.converted_valuation_currency
                                        )
                                    }}
                                </dd>
                            </div>
                            <div
                                class="text-xs bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                            >
                                <dt class="font-medium text-gray-500">
                                    Maximum Price
                                </dt>
                                <dd
                                    class="mt-1 text-gray-900 sm:mt-0 sm:col-span-2"
                                >
                                    {{
                                        $fabHelper.makeMoney(
                                            valuationDataConfig.time_series_data_per_carat_max,
                                            valuationParams.converted_valuation_currency
                                        )
                                    }}
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>
                <div class="bg-white shadow overflow-hidden sm:rounded-lg">
                    <div class="px-4 py-3 sm:px-6">
                        <p
                            class="font-bold mt-1 max-w-2xl text-sm text-gray-500"
                        >
                            Total ({{ newValuationRequest.carat }}
                            Carat)
                        </p>
                    </div>
                    <div class="border-t border-gray-200 text-xs">
                        <dl>
                            <div
                                class="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                            >
                                <dt class="font-medium text-gray-500">
                                    Minimum Price
                                </dt>
                                <dd
                                    class="mt-1 text-gray-900 sm:mt-0 sm:col-span-2"
                                >
                                    {{
                                        $fabHelper.makeMoney(
                                            valuationDataConfig.time_series_data_min,
                                            valuationParams.converted_valuation_currency
                                        )
                                    }}
                                </dd>
                            </div>
                            <div
                                class="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                            >
                                <dt class="font-bold text-gray-500">
                                    Average Price
                                </dt>
                                <dd
                                    class="mt-1 text-gray-900 sm:mt-0 sm:col-span-2 font-bold"
                                >
                                    {{
                                        $fabHelper.makeMoney(
                                            valuationSummary.base_valuation_mid,
                                            valuationParams.converted_valuation_currency
                                        )
                                    }}
                                </dd>
                            </div>
                            <div
                                class="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                            >
                                <dt class="font-medium text-gray-500">
                                    Maximum Price
                                </dt>
                                <dd
                                    class="mt-1 text-gray-900 sm:mt-0 sm:col-span-2"
                                >
                                    {{
                                        $fabHelper.makeMoney(
                                            valuationDataConfig.time_series_data_max,
                                            valuationParams.converted_valuation_currency
                                        )
                                    }}
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="
                !isEmpty(valuationIndexOptions) &&
                (valuationEstimate.base_valuation ||
                    !isEmpty(valuationChartData))
            "
            class="my-4 grid grid-cols-3 gap-6 mb-4 bg-white overflow-hidden md:grid-cols-3"
        ></div>
        <data-loader
            v-if="isLoading"
            message="Calculating your estimate &amp; generating the historical chart..."
        ></data-loader>
        <div class="grid grid-cols-3 gap-6" v-if="!isLoading">
            <h4 class="col-span-3 flex">
                {{ indexTitle
                }}<Tooltip
                    v-if="
                        (resultType == 'chart' ||
                            (resultType == 'estimate' &&
                                valuationType == 'auction')) &&
                        resultTypeOptions[valuationType][resultType].helpText
                    "
                    :tooltip="
                        resultTypeOptions[valuationType][resultType].helpText
                    "
                ></Tooltip>

                <span
                    class="ml-4 cursor-pointer text-xs font-thin body-font"
                    @click="showSearch = !showSearch"
                    v-if="'base_valuation' in valuationEstimate"
                >
                    <span v-if="showSearch === false">+ Show</span>
                    <span v-else>- Hide</span> Estimator
                </span>
            </h4>
        </div>
        <transition name="fade">
            <form
                action=""
                v-if="showSearch === true && !isEmpty(valuationIndexOptions)"
            >
                <div class="col-span-3">
                    <notice notice-type="info"
                        >To achieve the most accurate result you must complete
                        all nine criteria as accurately as possible.</notice
                    >
                    <notice notice-type="info"
                        >The accepted Carat range of values for this estimator
                        is
                        {{ caratRange.min }}
                        -
                        {{ caratRange.max }}. Please contact Admin for diamonds
                        outside of these ranges.</notice
                    >
                </div>
                <div
                    :class="'flash-notice ' + resultNoticeType"
                    v-if="resultNotice != ''"
                >
                    {{ resultNotice }}
                </div>
                <div class="grid grid-cols-4 gap-4">
                    <div class="col-span-2 sm:col-span-2">
                        <div
                            :class="
                                'form-item select-single ' +
                                getClass('converted_valuation_currency')
                            "
                        >
                            <div class="flex">
                                <label
                                    >{{
                                        valuationIndexOptions
                                            .converted_valuation_currency.label
                                    }}:
                                </label>
                                <Tooltip
                                    v-if="
                                        getHelpText(
                                            'converted_valuation_currency'
                                        )
                                    "
                                    :tooltip="
                                        getHelpText(
                                            'converted_valuation_currency'
                                        )
                                    "
                                ></Tooltip>
                            </div>
                            <select
                                v-model="
                                    newValuationRequest.converted_valuation_currency
                                "
                                name="converted_valuation_currency"
                                class="form-select single"
                            >
                                <option value="">-- Select --</option>
                                <option
                                    v-for="(
                                        option, index
                                    ) in valuationIndexOptions
                                        .converted_valuation_currency.options"
                                    :value="index"
                                >
                                    {{ option.title }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="col-span-2 sm:col-span-2">
                        <div
                            :class="
                                'form-item select-single ' + getClass('shape')
                            "
                        >
                            <div class="flex">
                                <label
                                    >{{ valuationIndexOptions.shape.label }}:
                                </label>
                                <Tooltip
                                    v-if="getHelpText('shape')"
                                    :tooltip="getHelpText('shape')"
                                ></Tooltip>
                            </div>
                            <select
                                v-model="newValuationRequest.shape"
                                name="shape"
                                class="form-select single"
                            >
                                <option value="">-- Select --</option>
                                <option
                                    v-for="(
                                        option, index
                                    ) in valuationIndexOptions.shape.options"
                                    :value="index"
                                >
                                    {{ option.title }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div class="col-span-2 sm:col-span-2">
                        <div
                            :class="'form-item input-text ' + getClass('carat')"
                        >
                            <div class="flex">
                                <label
                                    >{{ valuationIndexOptions.carat.label }}:
                                </label>
                                <Tooltip
                                    v-if="getHelpText('carat')"
                                    :tooltip="getHelpText('carat')"
                                ></Tooltip>
                            </div>
                            <t-input
                                v-model="newValuationRequest.carat"
                                name="carat"
                            />
                            <div
                                v-if="!caratInRange"
                                class="text-xs text-red-800 my-2"
                            >
                                {{ getHelpText("carat") }}
                            </div>
                        </div>
                    </div>
                    <div class="col-span-2 sm:col-span-2">
                        <div
                            :class="
                                'form-item select-single ' + getClass('color')
                            "
                        >
                            <div class="flex">
                                <label
                                    >{{ valuationIndexOptions.color.label }}:
                                </label>
                                <Tooltip
                                    v-if="getHelpText('color')"
                                    :tooltip="getHelpText('color')"
                                ></Tooltip>
                            </div>
                            <select
                                v-model="newValuationRequest.color"
                                name="color"
                                class="form-select single"
                            >
                                <option value="">-- Select --</option>
                                <option
                                    v-for="(
                                        option, index
                                    ) in valuationIndexOptions.color.options"
                                    :value="index"
                                >
                                    {{ option.title }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div class="col-span-2 sm:col-span-2">
                        <div
                            :class="
                                'form-item select-single ' + getClass('clarity')
                            "
                        >
                            <div class="flex">
                                <label
                                    >{{ valuationIndexOptions.clarity.label }}:
                                </label>
                                <Tooltip
                                    v-if="getHelpText('clarity')"
                                    :tooltip="getHelpText('clarity')"
                                ></Tooltip>
                            </div>
                            <select
                                v-model="newValuationRequest.clarity"
                                name="clarity"
                                class="form-select single"
                            >
                                <option value="">-- Select --</option>
                                <option
                                    v-for="(
                                        option, index
                                    ) in valuationIndexOptions.clarity.options"
                                    :value="index"
                                >
                                    {{ option.title }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div class="col-span-2 sm:col-span-2">
                        <div
                            :class="
                                'form-item select-single ' +
                                getClass('jurisdiction')
                            "
                        >
                            <div class="flex">
                                <label
                                    >{{
                                        valuationIndexOptions.jurisdiction_alt
                                            .label
                                    }}:
                                </label>
                                <Tooltip
                                    v-if="getHelpText('jurisdiction')"
                                    :tooltip="getHelpText('jurisdiction')"
                                ></Tooltip>
                            </div>
                            <select
                                v-model="newValuationRequest.jurisdiction"
                                name="jurisdiction"
                                class="form-select single"
                            >
                                <option value="">-- Select --</option>
                                <option
                                    v-for="(
                                        option, index
                                    ) in valuationIndexOptions.jurisdiction_alt
                                        .options"
                                    :value="index"
                                    v-if="option[valuationType]"
                                >
                                    {{ option.title }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div class="col-span-4 sm:col-span-4">
                        <transition name="fade"
                            ><div
                                class="grid grid-cols-4 gap-4"
                                v-if="showAdvancedFields === true"
                            >
                                <div class="col-span-2 sm:col-span-2">
                                    <div
                                        :class="
                                            'form-item select-single ' +
                                            getClass('cut')
                                        "
                                    >
                                        <div class="flex">
                                            <label
                                                >{{
                                                    valuationIndexOptions.cut
                                                        .label
                                                }}:
                                            </label>
                                            <Tooltip
                                                v-if="getHelpText('cut')"
                                                :tooltip="getHelpText('cut')"
                                            ></Tooltip>
                                        </div>
                                        <select
                                            v-model="newValuationRequest.cut"
                                            name="cut"
                                            class="form-select single"
                                        >
                                            <option value="">
                                                -- Select --
                                            </option>
                                            <option
                                                v-for="(
                                                    option, index
                                                ) in valuationIndexOptions.cut
                                                    .options"
                                                :value="index"
                                            >
                                                {{ option.title }}
                                            </option>
                                        </select>
                                    </div>
                                </div>

                                <div class="col-span-2 sm:col-span-2">
                                    <div
                                        :class="
                                            'form-item select-single ' +
                                            getClass('symmetry')
                                        "
                                    >
                                        <div class="flex">
                                            <label
                                                >{{
                                                    valuationIndexOptions
                                                        .symmetry.label
                                                }}:
                                            </label>
                                            <Tooltip
                                                v-if="getHelpText('symmetry')"
                                                :tooltip="
                                                    getHelpText('symmetry')
                                                "
                                            ></Tooltip>
                                        </div>
                                        <select
                                            v-model="
                                                newValuationRequest.symmetry
                                            "
                                            name="symmetry"
                                            class="form-select single"
                                        >
                                            <option value="">
                                                -- Select --
                                            </option>
                                            <option
                                                v-for="(
                                                    option, index
                                                ) in valuationIndexOptions
                                                    .symmetry.options"
                                                :value="index"
                                            >
                                                {{ option.title }}
                                            </option>
                                        </select>
                                    </div>
                                </div>

                                <div class="col-span-2 sm:col-span-2">
                                    <div
                                        :class="
                                            'form-item select-single ' +
                                            getClass('polish')
                                        "
                                    >
                                        <div class="flex">
                                            <label
                                                >{{
                                                    valuationIndexOptions.polish
                                                        .label
                                                }}:
                                            </label>
                                            <Tooltip
                                                v-if="getHelpText('polish')"
                                                :tooltip="getHelpText('polish')"
                                            ></Tooltip>
                                        </div>
                                        <input
                                            type="hidden"
                                            name="type"
                                            v-model="newValuationRequest.type"
                                        />
                                        <select
                                            v-model="newValuationRequest.polish"
                                            name="polish"
                                            class="form-select single"
                                        >
                                            <option value="">
                                                -- Select --
                                            </option>
                                            <option
                                                v-for="(
                                                    option, index
                                                ) in valuationIndexOptions
                                                    .polish.options"
                                                :value="index"
                                            >
                                                {{ option.title }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                                <div class="col-span-2 sm:col-span-2">
                                    <div
                                        :class="
                                            'form-item select-single ' +
                                            getClass('fluorescence')
                                        "
                                    >
                                        <div class="flex">
                                            <label
                                                >{{
                                                    valuationIndexOptions
                                                        .fluorescence.label
                                                }}:
                                            </label>
                                            <Tooltip
                                                v-if="
                                                    getHelpText('fluorescence')
                                                "
                                                :tooltip="
                                                    getHelpText('fluorescence')
                                                "
                                            ></Tooltip>
                                        </div>
                                        <select
                                            v-model="
                                                newValuationRequest.fluorescence
                                            "
                                            name="fluorescence"
                                            class="form-select single"
                                        >
                                            <option value="">
                                                -- Select --
                                            </option>
                                            <option
                                                v-for="(
                                                    option, index
                                                ) in valuationIndexOptions
                                                    .fluorescence.options"
                                                :value="index"
                                            >
                                                {{ option.title }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                            </div></transition
                        >
                        <div
                            class="mt-4 col-span-2 sm:col-span-2 cursor-pointer text-xs"
                            @click="showAdvancedFields = !showAdvancedFields"
                            v-if="valuationType == 'wholesale'"
                        >
                            <span v-if="showAdvancedFields === false"
                                >+ Show</span
                            >
                            <span v-else>- Hide</span> Advanced Filters
                        </div>
                    </div>
                </div>
                <div class="submit-container">
                    <div class="order-last mt-4 md:order-first md:mt-0">
                        <t-button
                            v-if="isValid"
                            @click="clearSaved()"
                            variant="secondaryCreate"
                            type="reset"
                            ><span>Clear Estimator</span>
                            <i class="ml-2 far fa-undo-alt"></i>
                        </t-button>
                    </div>
                    <div>
                        <t-button
                            :disabled="!isValid"
                            @click.prevent="runRequest()"
                            variant="secondaryCreate"
                            type="submit"
                            ><span>{{ resultTypeActionTitle }}</span>
                            <i class="ml-2 fal fa-dollar-sign"></i>
                        </t-button>
                    </div>
                </div>
            </form>
        </transition>
    </div>
</template>

<script>
function initialState() {
    return {
        isLoading: true,
        // hasResult: false,
        showAdvancedFields: true,
        resultType: "estimate",
        resultTypeOptions: {
            auction: {
                estimate: {
                    title: "Auction Estimate",
                    helpText:
                        "Based on the latest international auction prices.",
                },
                chart: {
                    title: "Price Chart",
                    helpText:
                        "Track the historical auction price of a diamond over time.",
                },
            },
            wholesale: {
                estimate: {
                    title: "Wholesale Estimate",
                    helpText: "",
                },
                chart: {
                    title: "Price Chart",
                    helpText:
                        "Track the historical wholesale price of a diamond over time",
                },
            },
        },
        showSearch: true,
        searchHasRun: false,
        showAllResults: true,
        resultNotice: "",
        resultNoticeType: "success",
        valuationEstimate: {},
        valuationParams: {},
        valuationSummary: {},
        valuationDataConfig: {},
        valuationChartData: {},
        newValuationRequest: {},
    };
}

import _startCase from "lodash/startCase";
import _isEmpty from "lodash/isEmpty";
import _inRange from "lodash/inRange";
import _max from "lodash/max";
import _ceil from "lodash/ceil";
import _round from "lodash/round";
import { mapGetters } from "vuex";
import { defaultValuationRequest } from "../../../store/state";
export default {
    props: {
        valuationType: {
            type: String,
            required: false,
            default: "",
        },
        source: {
            type: String,
            required: true,
            default: "",
        },
    },
    data() {
        return initialState();
    },
    mounted() {
        this.getValuationOptions();
        // Always start blank at defaults. The estimator never restores a
        // previous query — the form is reset after every submit and a page
        // refresh begins fresh (FCI-91/FCI-92). Starting blank also means the
        // two estimators can never cross-populate each other.
        this.newValuationRequest = defaultValuationRequest(this.valuationType);
        this.$store.dispatch("fetchUser");
    },

    watch: {
        user() {
            this.newValuationRequest.converted_valuation_currency =
                this.defaultCurrency;
        },
        valuationIndexOptions() {
            if (this.isValid) {
                this.runRequest();
            }
        },
        dateRange() {
            this.getValuation();
        },
    },
    methods: {
        getValuationOptions() {
            this.isLoading = true;

            return this.$store
                .dispatch("fetchValuationIndexOptions", [])
                .then((res) => {
                    this.isLoading = false;
                });
        },
        runRequest() {
            var th = this;
            var chain;
            if (this.showAllResults) {
                chain = this.getValuation().then(function (response) {
                    return th.getEstimate();
                });
            } else if (this.resultType == "estimate") {
                chain = this.getEstimate();
            } else {
                chain = this.getValuation();
            }
            return chain.then(function (response) {
                // Clear the filter inputs after a successful submit, leaving the
                // estimate and chart on screen (FCI-91/FCI-92). The query is
                // never persisted, so reloading the page also starts blank.
                th.resetForm();
                window.scrollTo(0, 0);
            });
        },
        getEstimate() {
            this.isLoading = true;
            return this.$store
                .dispatch("fetchValuationEstimate", this.newValuationRequest)
                .then((res) => {
                    this.resultType = "estimate";
                    this.valuationEstimate = this.valuationEstimateResult;

                    this.$EventBus.$emit("estimate-update", []);

                    this.isLoading = false;
                    this.searchHasRun = true;
                });
        },
        getValuation() {
            if (!this.isValid) return;
            this.isLoading = true;

            return this.$store
                .dispatch("fetchValuationIndex", this.newValuationRequest)
                .then((res) => {
                    this.resultType = "chart";
                    //parse the result data into the format needed for display
                    this.valuationParams =
                        this.valuationIndexResult.valuation_parameters;

                    this.valuationSummary = this.valuationIndexResult.valuation;
                    this.valuationDataConfig =
                        this.valuationIndexResult.time_series_data.config;
                    this.valuationChartData.labels =
                        this.valuationIndexResult.time_series_data.keys;
                    this.valuationChartData.valuationCurrencyPrefix =
                        this.valuationParams.converted_valuation_currency;
                    this.valuationChartData.datasets = [];
                    this.valuationChartData.datasets.push({
                        label: this.indexTitle,
                        data: this.valuationIndexResult.time_series_data.data,
                        //you would add any dataset-specific options in here as well
                        timeseries_data_max: this.getAdjustedRange(
                            this.valuationDataConfig.time_series_data_max,
                            "max"
                        ),
                        timeseries_data_min: this.getAdjustedRange(
                            this.valuationDataConfig.time_series_data_min,
                            "min"
                        ),
                    });

                    this.$EventBus.$emit("valuation-update", []);

                    this.isLoading = false;
                    this.searchHasRun = true;
                });
        },
        isEmpty(val) {
            return _isEmpty(val);
        },
        roundCarat(val) {
            return val; //_round(val, 2);
        },
        getHelpText(field) {
            let helpText = this.valuationIndexOptions[field].helpText[
                this.source
            ]
                ? this.valuationIndexOptions[field].helpText[this.source].create
                : "";
            return helpText.length > 0 ? helpText : false;
        },
        getClass(field) {
            let elementClass = this.valuationIndexOptions[field].class[
                this.source
            ]
                ? this.valuationIndexOptions[field].class[this.source].create
                : "";
            return elementClass.length > 0 ? elementClass : false;
        },
        getAdjustedRange(value, type) {
            if (type == "min") {
                return _ceil(_max([0, value * 0.95]));
            } else {
                return _ceil(value * 1.05);
            }
        },
        resetForm() {
            // Reset every filter input to its default state, with currency back
            // to the user default — matching a fresh estimator (FCI-91/FCI-92).
            // Used after each submit and by Clear. The query is never persisted.
            const cleared = defaultValuationRequest(this.valuationType);
            cleared.converted_valuation_currency = this.user.userCurrency || "";
            this.newValuationRequest = cleared;
        },
        clearResults() {
            // Remove the displayed estimate and chart.
            this.valuationEstimate = {};
            this.valuationParams = {};
            this.valuationSummary = {};
            this.valuationChartData = {};
            this.searchHasRun = false;
            this.resultType = "estimate";
        },
        clearSaved() {
            // Clear Estimator resets the whole form AND removes the results.
            this.resetForm();
            this.clearResults();
        },
    },
    computed: {
        ...mapGetters([
            "user",
            "valuationIndexOptions",
            "valuationIndexResult",
            "valuationEstimateResult",
        ]),

        defaultCurrency() {
            // No persisted query to honour — always the user default currency.
            return this.user.userCurrency;
        },
        indexTitle() {
            if (!this.valuationIndexOptions.type.options[this.valuationType]) {
                return "n/a";
            }
            if (this.resultType == "estimate") {
                this.valuationIndexOptions.type[this.valuationType];
                return (
                    this.valuationIndexOptions.type.options[this.valuationType]
                        .title + " Estimator"
                );
            } else {
                return (
                    this.valuationIndexOptions.type.options[this.valuationType]
                        .title + " Price Chart"
                );
            }
        },
        resultPriceNotice() {
            return "Chart prices are total figures.";
        },
        resultTypeActionTitle() {
            if (this.isValid) {
                return (
                    "Get " +
                    this.valuationIndexOptions.type.options[this.valuationType]
                        .title +
                    " Estimate & Price Chart"
                );
            } else {
                return "Enter the Required Data";
            }
        },
        valuationCurrencyTitle() {
            return this.valuationIndexOptions.converted_valuation_currency
                .options[this.valuationParams.converted_valuation_currency]
                .title;
        },
        estimateCurrencyTitle() {
            return this.valuationIndexOptions.converted_valuation_currency
                .options[
                this.valuationEstimateResult.parameters
                    .converted_valuation_currency
            ].title;
        },
        valuationDateRangeTitle() {
            let date_range =
                this.valuationIndexOptions.date_range.options[
                    this.valuationParams.date_range
                ].title;

            return date_range;
        },
        valuationIntervalTitle() {
            let interval = this.valuationDataConfig.interval;
            switch (interval) {
                case "hour":
                    var intervalTitle = "Hourly";
                    break;
                case "day":
                    var intervalTitle = "Daily";
                    break;
                case "week":
                    var intervalTitle = "Weekly";
                    break;
            }
            return intervalTitle;
        },
        dateNow() {
            let now = this.$dayjs().format("D MMM YYYY");
            return now;
        },
        caratInRange() {
            var carat = this.roundCarat(this.newValuationRequest.carat);

            var max = this.caratRange.max + 0.001;
            var min = this.caratRange.min;

            return this.newValuationRequest.carat.length == 0 ||
                _inRange(carat, min, max)
                ? true
                : false;
        },
        caratRange() {
            return this.valuationIndexOptions.type
                ? this.valuationIndexOptions.type.options[
                      this.newValuationRequest.type
                  ].carat_range
                : "";
        },
        isValid() {
            //round carat to 2 dec places
            this.newValuationRequest.carat = this.roundCarat(
                this.newValuationRequest.carat
            );

            return (
                this.newValuationRequest.type !== "" &&
                this.newValuationRequest.date_range !== "" &&
                this.newValuationRequest.converted_valuation_currency !== "" &&
                this.newValuationRequest.clarity !== "" &&
                this.newValuationRequest.carat !== "" &&
                _inRange(
                    this.newValuationRequest.carat,
                    this.caratRange.min - 0.01,
                    this.caratRange.max + 0.01
                ) &&
                this.newValuationRequest.color !== "" &&
                this.newValuationRequest.shape !== ""
            );
        },
        dateRange() {
            return this.newValuationRequest.date_range;
        },
    },
};
</script>

<style></style>
